import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Sparkles, Lock, MessageCircle, Eye, Gift, Zap, Phone, X, Crown } from "lucide-react";

const PLANS = [
  { key: "trial",     name: "Trial",           price:   99, original:  168, per: "10 days",         contacts: "5 contact numbers",  features: ["First-time premium members only", "View up to 5 contact numbers", "Unlock all photos", "No auto-renew"], gradient: "from-[#38BDF8] via-[#0EA5E9] to-[#0369A1]", accent: "#0369A1", badge: "New user only" },
  { key: "monthly",   name: "Monthly Premium", price:  299, original:  509, per: "1 month",         contacts: "10 contact numbers", features: ["View up to 10 contact numbers per month", "Unlock all 10 photos", "Priority in search", "Unlimited messaging"], gradient: "from-[#94A3B8] via-[#64748B] to-[#475569]", accent: "#64748B" },
  { key: "founder",   name: "Founder Pass",    price:  999, original: 1698, per: "Till marriage",   contacts: "10 contacts / month",  features: ["Male ₹999 · Female ₹499", "10 contact numbers every month", "Featured 'Founder' badge on profile", "Personal matchmaker touch", "First 1000 members only"], gradient: "from-[#F59E0B] via-[#DC2626] to-[#7C2D12]", accent: "#DC2626", featured: true, badge: "Best value" },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Rolling 24h countdown — deadline stored in localStorage, resets when it hits 0
function useOfferCountdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const key = "umg_offer_deadline";
  let deadline = parseInt(localStorage.getItem(key) || "0", 10);
  if (!deadline || deadline <= now) {
    deadline = now + 24 * 60 * 60 * 1000;
    localStorage.setItem(key, String(deadline));
  }
  const ms = Math.max(0, deadline - now);
  return {
    hours: Math.floor(ms / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
    seconds: Math.floor((ms % 60_000) / 1000),
  };
}

const pad = (n) => String(n).padStart(2, "0");

function CountdownPill({ variant = "light" }) {
  const { hours, minutes, seconds } = useOfferCountdown();
  const box = variant === "light"
    ? "bg-white/20 backdrop-blur text-white"
    : "bg-white text-[#B91C1C]";
  const cell = variant === "light"
    ? "bg-white/25 backdrop-blur"
    : "bg-[#FEE2E2]";
  return (
    <div data-testid="offer-countdown" className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 ${box}`} aria-live="polite">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2M9 2h6"/></svg>
      {[
        ["HRS", pad(hours)],
        ["MIN", pad(minutes)],
        ["SEC", pad(seconds)],
      ].map(([lbl, val], i, arr) => (
        <React.Fragment key={lbl}>
          <span className={`inline-flex flex-col items-center leading-none rounded-md px-1.5 py-1 ${cell}`}>
            <span className="font-mono font-bold text-sm tabular-nums">{val}</span>
            <span className="text-[7px] tracking-widest opacity-70 mt-0.5">{lbl}</span>
          </span>
          {i < arr.length - 1 && <span className="font-mono font-bold text-sm opacity-70">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Premium() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [showOffer, setShowOffer] = useState(false);
  const [founder, setFounder] = useState(null);

  useEffect(() => { loadRazorpay(); }, []);

  useEffect(() => {
    api.get("/premium/founder-status")
      .then(({ data }) => setFounder(data))
      .catch(() => {});
  }, [user?.is_premium]);

  // Show 50% OFF popup on page open (once per session)
  useEffect(() => {
    if (user?.is_premium) return;
    if (sessionStorage.getItem("umg_offer_seen") === "1") return;
    const t = setTimeout(() => {
      setShowOffer(true);
      sessionStorage.setItem("umg_offer_seen", "1");
    }, 700);
    return () => clearTimeout(t);
  }, [user?.is_premium]);

  const startCheckout = async (plan) => {
    try {
      const { data: order } = await api.post("/premium/order", { plan: plan.key });
      if (order.mocked || !order.key_id) {
        await api.post("/premium/verify", { order_id: order.order_id });
        toast.success(`Welcome to Umaang ${plan.name} ✨ (mock payment)`);
        await refresh();
        nav("/home");
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Umaang Matrimony",
        description: `${plan.name} membership · ${plan.per}`,
        order_id: order.order_id,
        prefill: order.prefill,
        theme: { color: "#C85A53" },
        handler: async (resp) => {
          try {
            await api.post("/premium/verify", resp);
            toast.success(`Welcome to Umaang ${plan.name} ✨`);
            await refresh();
            nav("/home");
          } catch (e) { toast.error(formatError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      {/* Founder ₹199 launch offer — first 1000 members, till marriage */}
      {founder?.eligible && !user?.is_premium && (
        <div data-testid="founder-banner" className="mb-6 relative overflow-hidden rounded-3xl p-5 sm:p-6 text-white" style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E293B 40%,#7C2D12 100%)" }}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FBBF24 0, transparent 40%), radial-gradient(circle at 90% 80%, #EC4899 0, transparent 40%)" }} />
          <div className="relative grid sm:grid-cols-[1fr_auto] gap-5 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-black px-3 py-1 rounded-full font-bold">
                <Crown className="w-3 h-3" /> Founder pass · Launch offer
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3">
                <span className="text-[#FBBF24]">₹199</span> <span className="text-base sm:text-lg font-normal opacity-80">+ ₹35.82 GST = ₹234.82</span> — Till your marriage
              </h2>
              <p className="text-sm sm:text-base opacity-90 mt-2 max-w-md">
                Only for Umaang's first <b className="text-[#FDE68A]">{founder.founder_limit}</b> members. Full Premium (unlimited contacts, unlock photos, family chat & more) until you find the one — no renewals, no upsell.
              </p>
              <div className="mt-3 inline-flex items-center gap-2 text-xs bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span><b>{founder.founder_remaining}</b> / {founder.founder_limit} slots left</span>
              </div>
            </div>
            <div className="flex sm:flex-col items-stretch gap-2 sm:min-w-[180px]">
              <button
                data-testid="founder-claim-btn"
                onClick={() => startCheckout({ key: "founder", name: "Founder", per: "till marriage" })}
                className="btn-pill bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] hover:from-[#F59E0B] hover:to-[#D97706] text-black font-bold px-6 py-3 rounded-full shadow-lg shadow-amber-500/30 text-sm sm:text-base"
              >
                Claim founder pass →
              </button>
              <div className="text-[10px] text-white/70 text-center sm:text-right">
                One-time · No auto-renew
              </div>
            </div>
          </div>
        </div>
      )}

      {founder && !founder.founder_active && !user?.is_premium && (
        <div data-testid="founder-closed" className="mb-6 p-3 rounded-full bg-[#F3F4F6] text-center text-xs text-[color:var(--umg-text-2)]">
          Founder pass sold out — all {founder.founder_limit} slots claimed. Regular plans below.
        </div>
      )}

      {/* 50% OFF slim banner (always visible above plans) */}
      {!user?.is_premium && (
        <div data-testid="offer-banner" className="mb-8 flex flex-wrap items-center justify-between gap-3 p-3 pl-5 rounded-full bg-gradient-to-r from-[#DC2626] via-[#DB2777] to-[#7C3AED] text-white shadow-lg shadow-[#DB2777]/30">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Zap className="w-4 h-4" fill="white" />
            <span className="font-semibold">Limited-time offer</span>
            <span className="opacity-90 hidden sm:inline">— 70% OFF ends in</span>
            <span className="opacity-90 sm:hidden">— ends in</span>
            <CountdownPill variant="light" />
          </div>
          <button data-testid="offer-view-plans" onClick={() => window.scrollTo({ top: 500, behavior: "smooth" })} className="text-xs uppercase tracking-widest bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full">
            See plans →
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Umaang Premium</div>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            One decision.<br/>A lifetime of connection.
          </h1>
          <p className="mt-5 text-[color:var(--umg-text-2)] leading-relaxed max-w-lg">
            Unlock every photo, view real contact numbers, and message anyone. Pick the plan that fits your journey.
          </p>
          <div className="mt-6 space-y-2 text-sm">
            {[
              [Lock, "Unlock all 10 photos on every profile"],
              [Phone, "View real contact numbers (plan-limited)"],
              [MessageCircle, "Unlimited messages, all profiles"],
              [Sparkles, "Verified badge · priority listing"],
            ].map(([I, txt]) => (
              <div key={txt} className="flex items-center gap-2"><I className="w-4 h-4 text-[#C85A53]" />{txt}</div>
            ))}
          </div>

          <div className="mt-6 p-4 border border-[color:var(--umg-border)] flex items-start gap-3" style={{ borderRadius: 8, background: "var(--umg-surface)" }}>
            <Gift className="w-5 h-5 text-[#C85A53] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold">Get it free.</div>
              <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">
                Refer 3 verified friends and earn a free premium month.{" "}
                <button onClick={() => nav("/referrals")} className="text-[#C85A53] underline">See referrals →</button>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#F59E0B]/25 via-[#DB2777]/20 to-[#7C3AED]/20 blur-2xl" aria-hidden />
          <div className="relative aspect-[4/5] overflow-hidden border border-[color:var(--umg-border)] shadow-2xl" style={{ borderRadius: 16 }}>
            <img
              src="https://images.pexels.com/photos/30541169/pexels-photo-30541169.jpeg"
              alt="premium"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((p) => (
          <div
            key={p.key}
            data-testid={`plan-${p.key}`}
            className={`p-6 border card-hover relative overflow-hidden ${p.featured ? `bg-gradient-to-br ${p.gradient} text-white border-transparent` : "border-[color:var(--umg-border)]"}`}
            style={{ borderRadius: 14, background: p.featured ? undefined : "var(--umg-surface)", boxShadow: p.featured ? `0 20px 40px ${p.accent}55` : undefined }}
          >
            {p.featured && (
              <div className="absolute top-3 right-3 bg-white/25 backdrop-blur px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest">Popular</div>
            )}
            <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15" style={{ background: p.accent }} />
            <div className="relative">
              <div className={`uppercase tracking-[0.28em] text-xs font-semibold ${p.featured ? "text-white/90" : ""}`} style={!p.featured ? { color: p.accent } : {}}>
                {p.name}
              </div>
              <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                <div className="font-display text-4xl">₹{p.price}</div>
                <div className={`text-sm line-through ${p.featured ? "text-white/60" : "text-[color:var(--umg-text-2)]"}`}>₹{p.original}</div>
              </div>
              <div className={`text-[11px] mt-1 ${p.featured ? "text-white/85" : "text-[color:var(--umg-text-2)]"}`}>
                + ₹{(p.price * 0.18).toFixed(2)} GST (18%) · Total <b>₹{(p.price * 1.18).toFixed(2)}</b>
              </div>
              <div className={`text-xs mt-2 font-semibold ${p.featured ? "text-white/90" : "text-[#DC2626]"}`}>70% OFF · {p.per}</div>
              <div className={`text-[11px] mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full ${p.featured ? "bg-white/20 text-white" : "bg-[color:var(--umg-secondary)]"}`}>
                <Phone className="w-3 h-3" /> {p.contacts}
              </div>
              <ul className="mt-4 space-y-1.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-white" : "text-[#4F7942]"}`} />
                    <span className={p.featured ? "" : "text-[color:var(--umg-text)]"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`subscribe-${p.key}`}
                onClick={() => startCheckout(p)}
                disabled={user?.is_premium}
                className={`w-full mt-6 btn-pill h-11 ${
                  p.featured
                    ? "bg-white text-[#B91C1C] hover:bg-white/90 font-semibold"
                    : "bg-[#1C1917] text-white hover:bg-[#111]"
                }`}
              >
                {user?.is_premium ? "You're Premium" : "Subscribe"}
              </Button>
              <div className={`mt-3 text-[10px] uppercase tracking-widest text-center ${p.featured ? "text-white/70" : "text-[color:var(--umg-text-2)]"}`}>
                Secured by Razorpay
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Still need help — support contact */}
      <div data-testid="support-block" className="mt-14 p-8 relative overflow-hidden border" style={{ borderRadius: 16, borderColor: "var(--umg-border)", background: "linear-gradient(135deg, #FEF3C7 0%, #FCE7F3 50%, #E9D5FF 100%)" }}>
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/40 blur-3xl" aria-hidden />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#C85A53]/15 blur-3xl" aria-hidden />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 shrink-0 rounded-2xl grid place-items-center bg-gradient-to-br from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white shadow-lg shadow-[#DB2777]/40">
              <Phone className="w-6 h-6" strokeWidth={2} />
            </div>
            <div>
              <div className="uppercase tracking-[0.28em] text-[10px] text-[#7C2D12]/70 font-semibold">Support</div>
              <h3 className="font-display text-2xl md:text-3xl mt-1 text-[#7C2D12]">Still need help?</h3>
              <p className="mt-2 text-sm text-[#57534E] leading-relaxed max-w-lg">
                We are right here to help you. Give us a call anytime between <b className="text-[#7C2D12]">10&nbsp;am to 7&nbsp;pm</b>.
              </p>
            </div>
          </div>
          <a
            href="tel:+917431004100"
            data-testid="support-call-btn"
            className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white font-semibold shadow-lg shadow-[#DB2777]/40 hover:opacity-90 transition"
          >
            <Phone className="w-4 h-4" fill="white" />
            <span className="text-sm">
              <span className="opacity-80 mr-1">India</span>+91-74 3100 4100
            </span>
          </a>
        </div>
      </div>

      {/* 50% OFF Popup */}
      <Dialog open={showOffer} onOpenChange={setShowOffer}>
        <DialogContent data-testid="offer-modal" className="max-w-md border-0 p-0 overflow-hidden">
          <div className="bg-gradient-to-br from-[#DC2626] via-[#DB2777] to-[#7C3AED] p-8 text-white text-center relative">
            <button onClick={() => setShowOffer(false)} className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="offer-close">
              <X className="w-4 h-4" />
            </button>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[10px] uppercase tracking-widest">
              <Zap className="w-3 h-3" fill="white" /> Limited time
            </div>
            <div className="font-display text-6xl mt-4">70% OFF</div>
            <div className="mt-2 text-lg font-semibold">on every Premium plan</div>
            <div className="mt-1 text-sm opacity-90">Silver to Diamond — every plan discounted.</div>
            <div className="mt-4 flex flex-col items-center gap-1.5">
              <div className="text-[10px] uppercase tracking-[0.28em] opacity-90">Offer ends in</div>
              <CountdownPill variant="dark" />
            </div>
            <div className="mt-6 grid grid-cols-4 gap-2 text-xs">
              {PLANS.map((p) => (
                <div key={p.key} className="bg-white/15 backdrop-blur rounded-lg py-2 px-1">
                  <div className="text-[9px] uppercase tracking-widest opacity-80">{p.name}</div>
                  <div className="font-display text-lg mt-0.5">₹{p.price}</div>
                  <div className="text-[9px] opacity-70">+GST</div>
                </div>
              ))}
            </div>
            <Button
              data-testid="offer-cta"
              onClick={() => { setShowOffer(false); window.scrollTo({ top: 500, behavior: "smooth" }); }}
              className="mt-6 w-full btn-pill bg-white text-[#B91C1C] hover:bg-white/90 h-11 font-semibold"
            >
              Grab the offer →
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
